import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronDown, CalendarX2, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const fmtData = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function ContrattiScadutiPanel({ month }) {
  const { selectedStructure } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const sid = selectedStructure && selectedStructure !== "all" ? selectedStructure : undefined;

  const { data: attivi = [] } = useQuery({
    queryKey: ["contr-attivi", selectedStructure],
    queryFn: async () => (await api.get("/operatori", { params: { structure_id: selectedStructure || "all" } })).data,
  });
  const { data: archivio = [] } = useQuery({
    queryKey: ["contr-archivio", selectedStructure],
    queryFn: async () => { try { return (await api.get("/operatori/archivio", { params: sid ? { structure_id: sid } : {} })).data; } catch { return []; } },
  });

  const byId = {};
  [...attivi, ...archivio].forEach((o) => { if (o && o.id) byId[o.id] = o; });
  // Mese di riferimento = mese visualizzato nella lista (fallback: mese corrente); "mese scorso" = quello precedente
  const base = (typeof month === "string" && /^\d{4}-\d{2}$/.test(month))
    ? new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1)
    : new Date();
  const prev = new Date(base.getFullYear(), base.getMonth() - 1, 1);
  const ym = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
  const meseLabel = `${MESI[prev.getMonth()]} ${prev.getFullYear()}`;

  const scaduti = Object.values(byId)
    .map((o) => ({ o, end: (o.data_cessazione || o.scadenza_contratto || "").slice(0, 10) }))
    .filter((x) => x.end && x.end.slice(0, 7) === ym)
    .sort((a, b) => (a.o.cognome || "").localeCompare(b.o.cognome || "", "it") || (a.o.nome || "").localeCompare(b.o.nome || "", "it"));

  return (
    <div className="mt-4" data-testid="contratti-scaduti-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="contratti-scaduti-toggle"
        className="w-full flex items-center gap-2 text-left px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100/70 transition-colors">
        <ChevronDown size={16} className={`text-amber-700 transition-transform ${open ? "" : "-rotate-90"}`} />
        <CalendarX2 size={17} className="text-amber-700 shrink-0" />
        <span className="font-bold text-sm text-amber-900">Contratti scaduti il mese scorso <span className="font-semibold text-amber-700 capitalize">· {meseLabel}</span></span>
        <span className="ml-auto text-xs font-bold text-amber-800 bg-amber-200 rounded-full px-2.5 py-0.5 tabnum" data-testid="contratti-scaduti-count">{scaduti.length}</span>
      </button>
      {open && (
        <div className="mt-2 gr-card p-0 overflow-hidden" data-testid="contratti-scaduti-list">
          {scaduti.length === 0 ? (
            <p className="text-sm text-muted-foreground px-4 py-6 text-center" data-testid="contratti-scaduti-empty">Nessun contratto scaduto a {meseLabel}.</p>
          ) : scaduti.map(({ o, end }) => (
            <button key={o.id} type="button" onClick={() => navigate(`/operatori/${o.id}`)} data-testid={`contratto-scaduto-${o.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 border-b last:border-0 hover:bg-secondary/50 transition-colors text-left">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-primary truncate">{o.cognome} {o.nome}</div>
                <div className="text-xs text-muted-foreground">{o.qualifica || "operatore"} · scaduto il {fmtData(end)}</div>
              </div>
              <ChevronRight size={16} className="text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
